import { AuthenticatedHttp } from '../../http/axios';
import { AppError, parsePaginationResponse } from '../../utils/api';
import { downloadFile } from '../../utils/files';
import { CACHE_TTL } from '../constants';

const cache = new Map();

function normalizeValue(v) {
  if (Array.isArray(v)) return [...v].sort();
  if (v === undefined || v === null) return '';
  return v;
}

function buildKey(params) {
  const normalized = {};
  Object.keys(params || {})
    .sort()
    .forEach((k) => {
      normalized[k] = normalizeValue(params[k]);
    });
  return JSON.stringify(normalized);
}

export const clearAffiliatesCache = () => {
  cache.clear();
};

function getCached(key, fetcher) {
  const now = Date.now();
  const cached = cache.get(key);

  if (cached) {
    if (cached.data && now - cached.ts < CACHE_TTL) return Promise.resolve(cached.data);
    if (cached.promise) return cached.promise;
  }

  const promise = fetcher()
    .then((data) => {
      cache.set(key, { data, ts: Date.now() });
      return data;
    })
    .catch((e) => {
      cache.delete(key);
      throw e;
    });

  cache.set(key, { promise, ts: now });

  return promise;
}

const buildFilterParams = ({ search, status, filter = {} }) => ({
  search,
  ...(filter?.products?.length > 0 && { productPk: filter.products }),
  ...(filter?.userEmail && { userEmail: filter.userEmail }),
  ...(status?.length > 0 && { status }),
});

export const getAffiliates = async ({
  page,
  search,
  productType,
  currency,
  status,
  limit = 10,
  filter = {},
  url = '',
  type = ''
}) => {
  const params = {
    page,
    limit,
    url: url || '',
    ...buildFilterParams({ search, status, filter }),
    ...(productType && { productType }),
    ...(currency && currency !== 'all' && { currency }),
    ...(type && { type }),
  };

  const key = `affiliates:${buildKey(params)}`;

  return getCached(key, () =>
    AuthenticatedHttp.get(`/affiliates/${url}`, { params })
      .then(parsePaginationResponse)
      .catch((error) => {
        throw new AppError(error);
      })
  );
};

// Fora do cache acima, que é por parâmetros e não pela conta: a resposta é da conta logada.
export const getOwnAffiliationsService = async ({ productPk, status, limit = 1 }) =>
  AuthenticatedHttp.get('/affiliates/', {
    params: { type: 'affiliate', productPk, status, limit },
  })
    .then(parsePaginationResponse)
    .catch((error) => {
      throw new AppError(error);
    });

// Mesmos filtros da lista, sem página: o arquivo traz tudo o que a busca e os filtros da tela
// alcançam. O backend fixa a visão do produtor, então não há `type` aqui.
export const exportAffiliatesService = async ({ extension, search, status, filter }) =>
  AuthenticatedHttp.get(`/affiliates/export/${extension}/`, {
    params: buildFilterParams({ search, status, filter }),
    responseType: 'blob',
  })
    .then((response) => {
      downloadFile({
        url: window.URL.createObjectURL(new Blob([response.data])),
        filename: `afiliados.${extension}`,
      });
    })
    .catch((error) => {
      throw new AppError(error);
    });

export const createAffiliateService = async ({ productPk, userEmail, isFromShowcase = false }) =>
  AuthenticatedHttp.post('/affiliates/', {
    productPk,
    userEmail,
    is_from_showcase: isFromShowcase,
  })
    .then((response) => {
      clearAffiliatesCache();
      return response.data;
    })
    .catch((error) => {
      throw new AppError(error);
    });

export const updateAffiliateService = async ({ id, info }) =>
  AuthenticatedHttp.put(`/affiliates/${id}/`, info)
    .then((response) => {
      clearAffiliatesCache();
      return response.data;
    })
    .catch((error) => {
      throw new AppError(error);
    });

export const acceptAffiliateService = async (ids) =>
  AuthenticatedHttp.post(`/affiliates/accept_many/`, { ids })
    .then((response) => {
      clearAffiliatesCache();
      return response.data;
    })
    .catch((error) => {
      throw new AppError(error);
    });

export const blockAffiliateService = async (ids) =>
  AuthenticatedHttp.post(`/affiliates/block_many/`, { ids })
    .then((response) => {
      clearAffiliatesCache();
      return response.data;
    })
    .catch((error) => {
      throw new AppError(error);
    });

export const unblockAffiliateService = async (ids) =>
  AuthenticatedHttp.post(`/affiliates/unblock_many/`, { ids })
    .then((response) => {
      clearAffiliatesCache();
      return response.data;
    })
    .catch((error) => {
      throw new AppError(error);
    });

export const rejectAffiliateService = async (ids) =>
  AuthenticatedHttp.post(`/affiliates/reject_many/`, { ids })
    .then((response) => {
      clearAffiliatesCache();
      return response.data;
    })
    .catch((error) => {
      throw new AppError(error);
    });

export const deleteAffiliateService = async ({ id }) =>
  AuthenticatedHttp.delete(`/affiliates/${id}/`)
    .then((response) => {
      clearAffiliatesCache();
      return response.data;
    })
    .catch((error) => {
      throw new AppError(error);
    });
